class AddressList extends HTMLElement {
  static observedAttributes = ["data-mode"];
  static modes = ["view", "edit", "add"];

  constructor() {
    super();

    this.viewEl = this.querySelector(".js-view");
    this.listEl = this.querySelector(".js-list");
    this.editHeadingEl = this.querySelector('.js-edit-heading');
    this.addHeadingEl = this.querySelector('.js-add-heading');
    this.form = this.querySelector(".js-form");
    this.provinceInputEl = this.querySelector(".js-province-input");
    this.mode = this.dataset.mode ? this.dataset.mode : "view";
    this.addressesCountEl = this.querySelector('.js-addresses-count');
    this.addBtns = this.querySelectorAll(".js-add");
    this.cancelBtn = this.querySelector(".js-cancel");

    this.scrollContainer = this.closest(".js-scroll-container");
  }

  connectedCallback() {
    this._attachListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "data-mode":
        switch (newValue) {
          case "view":
            this.viewEl.setAttribute("aria-hidden", false);
            this.form.setAttribute("aria-hidden", true);
            this.editHeadingEl?.setAttribute("aria-hidden", false);
            this.addHeadingEl?.setAttribute("aria-hidden", false);
            break;
          case "edit":
            this.viewEl.setAttribute("aria-hidden", true);
            this.form.setAttribute("aria-hidden", false);
            this.editHeadingEl?.setAttribute("aria-hidden", false);
            this.addHeadingEl?.setAttribute("aria-hidden", true);
            break;
          case "add":
            this.viewEl.setAttribute("aria-hidden", true);
            this.form.setAttribute("aria-hidden", false);
            this.editHeadingEl?.setAttribute("aria-hidden", true);
            this.addHeadingEl?.setAttribute("aria-hidden", false);
            break;
        }
        if(this.scrollContainer)
        this.scrollContainer.scrollTop = 0;
        break;
    }
  }

  _attachListeners() {
    this.addBtns.forEach((btn) => btn.addEventListener("click", this._onAddBtnClick.bind(this)));
    this.cancelBtn.addEventListener("click", this._onCancelBtnClick.bind(this));
    this.form.addEventListener("submit", this._onFormSubmit.bind(this));
    this.addEventListener("address:delete", this._onDelete.bind(this));
    this.addEventListener("address:edit", this._onEdit.bind(this));
    this.form["address[country]"].addEventListener("change", (event) => {
      const selectEl = this.form["address[country]"];
      const selectedOption = selectEl.options[selectEl.selectedIndex];

      const provinces = JSON.parse(selectedOption.dataset.provinces);
      this.form["address[province]"].innerHTML = "";

      if (provinces.length > 0) {
        this.provinceInputEl.setAttribute("aria-hidden", false);
        provinces.forEach((province) => {
          const option = document.createElement("option");
          option.value = province[0];
          option.textContent = province[0];
          this.form["address[province]"].appendChild(option);
        });
      } else {
        this.provinceInputEl.setAttribute("aria-hidden", true);
      }
    });
  }

  _onAddBtnClick() {
    this.mode = "add";
    this._resetForm();
    this.form.action = "/account/addresses";
  }

  _onCancelBtnClick() {
    this.mode = "view";
    this._resetForm();
  }

  async _onDelete(event) {
    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    try {
      const response = await fetch(`/account/addresses/${event.detail.addressId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          _method: "delete",
        }),
      });

      if (response.ok) {
        this.update();
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }

  async _onEdit(event) {
    this.mode = "edit";
    this.form.action = `/account/addresses/${event.detail.addressId}`;
    this.form["address[first_name]"].value = event.detail.firstName;
    this.form["address[last_name]"].value = event.detail.lastName;
    this.form["address[company]"].value = event.detail.company;
    this.form["address[phone]"].value = event.detail.phone;
    this.form["address[address1]"].value = event.detail.address1;
    this.form["address[address2]"].value = event.detail.address2;
    this.form["address[city]"].value = event.detail.city;
    this.form["address[zip]"].value = event.detail.zip;
    this.form["address[country]"].value = event.detail.country;
    if (event.detail.province !== "") {
      this.provinceInputEl.setAttribute("aria-hidden", false);
      this.form["address[province]"].value = event.detail.province;
    }
    this.form["address[default]"].checked = event.detail.isDefault;
  }

  _updateList() {}

  _resetForm() {
    this.form.reset();
    this.provinceInputEl.setAttribute("aria-hidden", true);
  }

  async _onFormSubmit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    if (this.mode === "edit") {
      formData.append("_method", "put");
    }

    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    try {
      const response = await fetch(this.form.action, {
        method: this.form.method,
        body: formData,
      });

      if (response.ok) {
        await this.update();
        this.mode = "view";
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }

  async update() {
    const sectionResponse = await fetch(`?sections=${this.ajaxSection}`);
    const sectionJson = await sectionResponse.json();

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = sectionJson[Object.keys(sectionJson)[0]];
    const newListEl = tempDiv.querySelector(".js-list");
    const newAddressesCountEl = tempDiv.querySelector('.js-addresses-count');

    this.listEl.innerHTML = newListEl.innerHTML;
    this.listEl.querySelector(".js-add")?.addEventListener("click", this._onAddBtnClick.bind(this));
    this.addressesCountEl.innerHTML = newAddressesCountEl.innerHTML;
  }

  _fillForm(data) {}

  get mode() {
    return this.dataset.mode;
  }

  set mode(value) {
    if (AddressList.modes.includes(value)) {
      this.dataset.mode = value;
    } else {
      this.dataset.mode = this.modes[0];
    }
  }

  get ajaxSection() {
    return this.dataset.ajaxSection;
  }
}

class AddressItem extends HTMLElement {
  constructor() {
    super();

    this.editBtn = this.querySelector(".js-edit");
    this.deleteBtn = this.querySelector(".js-delete");

    this.data = JSON.parse(this.querySelector(".js-data").innerHTML);
  }

  connectedCallback() {
    this._attachListeners();
  }

  _attachListeners() {
    this.deleteBtn.addEventListener("click", async (e) =>
      this.dispatchEvent(
        new CustomEvent("address:delete", { detail: { addressId: this.addressId }, bubbles: true })
      )
    );
    this.editBtn.addEventListener("click", async (e) =>
      this.dispatchEvent(
        new CustomEvent("address:edit", {
          detail: { addressId: this.addressId, ...this.data },
          bubbles: true,
        })
      )
    );
  }

  get addressId() {
    return this.dataset.addressId;
  }
}

if (!customElements.get("address-list")) {
  customElements.define("address-list", AddressList);
}
if (!customElements.get("address-item")) {
  customElements.define("address-item", AddressItem);
}